/**
 * FilesView — the case folder (brief.md, inputs/, work/, outputs/) with a preview of the open file:
 * images inline, PDFs in a new tab, HTML rendered in a sandboxed iframe (never opened on the app
 * origin), text (CSV, scripts, Markdown, JSON) fetched and shown in mono. HTML, SVG and other active
 * content get a Download link instead of Open (agentUi.fileLinkProps).
 */

import { FileText, FileCode, Image as ImageIcon, FileJson, Folder } from 'lucide-react';
import { filePreviewKind, fileLinkProps } from './agentUi.js';
import useFileText from './useFileText.js';
import SandboxedHtml from './SandboxedHtml.jsx';
import FileLink from './FileLink.jsx';
import './WorkbenchDrawer.css';

const FOLDER_ORDER = ['', 'inputs', 'work', 'outputs'];

function iconFor(kind) {
  if (kind === 'png' || kind === 'jpg') return ImageIcon;
  if (kind === 'py' || kind === 'html') return FileCode;
  if (kind === 'json') return FileJson;
  return FileText;
}

const sizeText = (n) => {
  const b = Number(n) || 0;
  if (b >= 1048576) return `${(b / 1048576).toFixed(1)} MB`;
  if (b >= 1024) return `${Math.round(b / 1024)} KB`;
  return `${b} B`;
};

export default function FilesView({ files = [], openPath = null, fileUrl = null, onOpen }) {
  const groups = new Map();
  for (const f of files) {
    const top = f.path.includes('/') ? f.path.split('/')[0] : '';
    if (!groups.has(top)) groups.set(top, []);
    groups.get(top).push(f);
  }
  const keys = [...groups.keys()].sort((a, b) => {
    const ia = FOLDER_ORDER.indexOf(a);
    const ib = FOLDER_ORDER.indexOf(b);
    return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
  });

  const url = openPath && fileUrl ? fileUrl(openPath) : null;
  const kind = filePreviewKind(openPath);
  const textual = kind === 'text';
  const preview = useFileText(url, textual);
  const link = openPath ? fileLinkProps(openPath, url) : null;

  return (
    <div className="wbd__files">
      <ul className="wbd__tree wbd__scroller" aria-label="Case files">
        {files.length === 0 && <li className="wbd__empty">The case folder is empty.</li>}
        {keys.map((k) => (
          <li key={k || 'root'}>
            {k && <div className="wbd__folder"><Folder size={12} aria-hidden="true" /> {k}/</div>}
            <ul>
              {groups.get(k).map((f) => {
                const FileIcon = iconFor(f.kind);
                return (
                  <li key={f.path}>
                    <button
                      type="button"
                      className={`wbd__file${f.path === openPath ? ' is-open' : ''}`}
                      onClick={() => onOpen?.(f.path)}
                      title={f.path}
                    >
                      <FileIcon size={12} aria-hidden="true" />
                      <span className="wbd__fname">{k ? f.path.slice(k.length + 1) : f.path}</span>
                      <span className="wbd__fsize">{sizeText(f.size)}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
      </ul>

      <div className="wbd__preview wbd__scroller">
        {!openPath && <div className="wbd__empty">Pick a file to preview it.</div>}
        {openPath && (
          <div className="wbd__preview-head">
            <span className="wbd__fname">{openPath}</span>
            {link && <FileLink link={link} />}
          </div>
        )}
        {kind === 'image' && url && <img className="wbd__img" src={url} alt={openPath} />}
        {kind === 'pdf' && <div className="wbd__empty">PDF: use Open to view it in a new tab.</div>}
        {kind === 'html' && url && <SandboxedHtml url={url} title={openPath} className="wbd__pdf" />}
        {textual && preview.text !== null && <pre className="wbd__code">{preview.text}</pre>}
        {textual && preview.error && <div className="wbd__empty">Could not load the file ({preview.error}).</div>}
        {textual && preview.text === null && !preview.error && url && <div className="wbd__empty">Loading…</div>}
      </div>
    </div>
  );
}
